import React, { useCallback, useMemo } from 'react';
import { AutoActionIntent, IJoinedRoomState } from '../../Core/State/IJoinedRoomState';
import { IUserState } from '../../Core/State/IUserState';
import WindowPortal from './WindowPortal';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { classicTheme, modernTheme } from '../../Themes/PokerThemes';
import GameRoom from './GameRoom';
import { SnackbarProvider } from 'notistack';
import { Collapse } from '@mui/material';
import { isUserSeatedInRoom } from '../../Utils/RoomSeating';
import { TableReactionId } from '../../Core/State/ITableReactionState';

interface DetachedRoomProps {
  roomState: IJoinedRoomState;
  userState: IUserState;
  onClose: () => void;
  onPopupBlocked?: () => void;
  sendAction: (roomId: string, action: string, amount: number, userId: string) => void;
  leaveRoom: (roomId: string) => void;
  standFromRoom: (roomId: string) => void;
  switchToLobby: () => void;
  requestRoomState: (roomId: string) => void;
  sendBalanceUpdate: (roomId: string, userId: string, balance: number) => void;
  requestSitOptions: (roomId: string, seatIndex: number) => void;
  requestSit: (roomId: string, seatIndex: number, buyInAmount: number, postOrWaitChoice?: 'post' | 'wait') => void;
  updateRoomState: (data: { roomId: string; state: Partial<IJoinedRoomState>; }) => void;
  sitOnRoom: (roomId: string, seatIndex: number, amount: number) => void;
  requestShowCardsOption?: (roomId: string, cards: string[]) => void;
  requestBalanceOptions?: (roomId: string, userId: string) => void;
  requestTopUpNextHand?: (roomId: string) => void;
  requestTopUpAlways?: (roomId: string) => void;
  requestCancelTopUp?: (roomId: string) => void;
  requestSitOutNextHand?: (roomId: string) => void;
  requestCancelSitOutNextHand?: (roomId: string) => void;
  requestSitOutNextBb: (roomId: string) => void;
  requestCancelSitOutNextBb: (roomId: string) => void;
  requestSetAutoAction: (roomId: string, intent: AutoActionIntent, maxCallAmount?: number) => void;
  requestCancelAutoAction: (roomId: string) => void;
  requestSendTableReaction: (roomId: string, targetUserId: string, reactionId: TableReactionId) => void;
  requestSitBackIn?: (roomId: string) => void;
}

const getGameLabel = (type?: string): string => {
  if (type === 'aof') return 'AoF';
  if (type === 'texas') return "Hold'em NL";
  return 'Poker';
};

const getCleanTitlePart = (value?: string | null): string => (
  typeof value === 'string' ? value.trim() : ''
);

const getDetachedRoomTitle = (roomState: IJoinedRoomState): string => {
  const tableName = getCleanTitlePart(roomState.name) || `Table ${roomState.roomId}`;
  const tournamentName = getCleanTitlePart((roomState as { tournamentName?: string }).tournamentName);
  const gameLabel = getGameLabel(roomState.type);

  if (!roomState.isTournamentTable) {
    return `${tableName} - ${gameLabel}`;
  }

  const tableLabel = roomState.isFinalTournamentTable ? `${tableName} Final Table` : tableName;
  if (tournamentName && tournamentName !== tableName) {
    return `${tournamentName} - ${tableLabel}`;
  }

  return `${tableLabel} - MTT`;
};

const DetachedRoom: React.FC<DetachedRoomProps> = ({
  roomState,
  userState,
  onClose,
  onPopupBlocked,
  sendAction,
  leaveRoom,
  standFromRoom,
  switchToLobby,
  requestRoomState,
  sendBalanceUpdate,
  requestSitOptions,
  requestSit,
  updateRoomState,
  sitOnRoom,
  requestShowCardsOption,
  requestBalanceOptions,
  requestTopUpNextHand,
  requestTopUpAlways,
  requestCancelTopUp,
  requestSitOutNextHand,
  requestCancelSitOutNextHand,
  requestSitOutNextBb,
  requestCancelSitOutNextBb,
  requestSetAutoAction,
  requestCancelAutoAction,
  requestSendTableReaction,
  requestSitBackIn,
}) => {
  const isUserSeated = useMemo(
    () => isUserSeatedInRoom(roomState, userState.id),
    [roomState, userState.id]
  );
  const roomTitle = useMemo(
    () => getDetachedRoomTitle(roomState),
    [
      roomState.name,
      roomState.roomId,
      roomState.type,
      roomState.isTournamentTable,
      roomState.isFinalTournamentTable,
      (roomState as { tournamentName?: string }).tournamentName
    ]
  );

  // Handle window close
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  // Handle leave room with confirmation
  const handleLeaveRoom = () => {
    if (isUserSeated) {
      updateRoomState({
        roomId: roomState.roomId,
        state: { showLeaveRoomDialog: true }
      });
    } else {
      leaveRoom(roomState.roomId);
    }
  };

  // Handle stand from table with confirmation
  const handleStandFromTable = () => {
    standFromRoom(roomState.roomId);
  };

  // Handle switch to lobby
  const handleSwitchToLobby = () => {
    switchToLobby();
    handleClose();
  };

  // Handle actual stand from table (after confirmation)
  const handleConfirmStandFromTable = () => {
    standFromRoom(roomState.roomId);
    updateRoomState({
      roomId: roomState.roomId,
      state: { showLeaveSeatDialog: false }
    });
  };

  // Handle actual leave room (after confirmation)
  const handleConfirmLeaveRoom = () => {
    // Close the dialog first
    updateRoomState({
      roomId: roomState.roomId,
      state: { showLeaveRoomDialog: false }
    });
    // Send leave request
    leaveRoom(roomState.roomId);
  };

  const safeRequestShowCardsOption = useCallback((roomId: string, cards: string[]) => {
    requestShowCardsOption?.(roomId, cards);
  }, [requestShowCardsOption]);

  const safeRequestBalanceOptions = useCallback((roomId: string, userId: string) => {
    requestBalanceOptions?.(roomId, userId);
  }, [requestBalanceOptions]);

  const safeRequestTopUpNextHand = useCallback((roomId: string) => {
    requestTopUpNextHand?.(roomId);
  }, [requestTopUpNextHand]);

  const safeRequestTopUpAlways = useCallback((roomId: string) => {
    requestTopUpAlways?.(roomId);
  }, [requestTopUpAlways]);

  const safeRequestCancelTopUp = useCallback((roomId: string) => {
    requestCancelTopUp?.(roomId);
  }, [requestCancelTopUp]);

  const safeRequestSitOutNextHand = useCallback((roomId: string) => {
    requestSitOutNextHand?.(roomId);
  }, [requestSitOutNextHand]);

  const safeRequestCancelSitOutNextHand = useCallback((roomId: string) => {
    requestCancelSitOutNextHand?.(roomId);
  }, [requestCancelSitOutNextHand]);

  // Select theme based on room type
  const theme = roomState.type === 'texas' ? classicTheme : modernTheme;

  return (
    <WindowPortal
      title={roomTitle}
      onClose={handleClose}
      onPopupBlocked={onPopupBlocked}
      roomId={roomState.roomId}
      tileable
    >
      <SnackbarProvider
        maxSnack={5}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoHideDuration={5000}
        preventDuplicate
        dense
        TransitionComponent={Collapse}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GameRoom
            sendAction={sendAction}
            userState={userState}
            leaveRoom={handleLeaveRoom}
            standFromRoom={handleStandFromTable}
            switchToLobby={handleSwitchToLobby}
            requestRoomState={requestRoomState}
            roomState={roomState}
            isUserSeated={isUserSeated}
            isSingleRoomView={true}
            sendBalanceUpdate={sendBalanceUpdate}
            requestSitOptions={requestSitOptions}
            requestSit={requestSit}
            updateRoomState={updateRoomState}
            sitOnRoom={sitOnRoom}
            requestShowCardsOption={safeRequestShowCardsOption}
            requestBalanceOptions={safeRequestBalanceOptions}
            requestTopUpNextHand={safeRequestTopUpNextHand}
            requestTopUpAlways={safeRequestTopUpAlways}
            requestCancelTopUp={safeRequestCancelTopUp}
            requestSitOutNextHand={safeRequestSitOutNextHand}
            requestCancelSitOutNextHand={safeRequestCancelSitOutNextHand}
            isDetached={true}
            confirmLeaveRoom={handleConfirmLeaveRoom}
            requestSitOutNextBb={requestSitOutNextBb}
            requestCancelSitOutNextBb={requestCancelSitOutNextBb}
            requestSetAutoAction={requestSetAutoAction}
            requestCancelAutoAction={requestCancelAutoAction}
            requestSendTableReaction={requestSendTableReaction}
            requestSitBackIn={requestSitBackIn}
          />
        </ThemeProvider>
      </SnackbarProvider>
    </WindowPortal>
  );
};

export default DetachedRoom;
